import { useState } from "react";
import moment from "moment";
import { Alert, Button, Col, Modal, Row } from "react-bootstrap";
import { FieldValues, Path, PathValue, UseFormSetValue } from "react-hook-form";
import { AviTracerApi, IfrPlanningOverrides, IfrRouteFindInput, IfrRouteResult, ValidateFplResponse } from "../../../avitracerApi";
import Flight, { isIfrFlightRules } from "../../../models/flight";
import FlightPlan from "../../../models/flightPlan";
import { getFplCoordsFromRoutePoint, Route } from "../../../models/route";
import PlanIfrRouteModal, { IfrRouteSelectResult } from "../../PlanIfrRouteModal";
import ListRoutesPage from "../../../pages/organization/routes/ListRoutesPage";
import ReadOnlyInputBox from "../../InputBox/ReadonlyInputBox";
import { EntityDetailsDisplayInfoFieldIfrRoutePlanner } from "../EntityDetailsDisplayInfo";

export interface EntityIfrRoutePlannerProps<Entity extends FieldValues> {
  displayInfoInput: EntityDetailsDisplayInfoFieldIfrRoutePlanner<Entity>;
  entityType: "Flight" | "Flight Plan";
  edittedEntity: Entity;
  setValue: UseFormSetValue<Entity>;
}

// IFR route planning inside the edit form's Route section.
//
// Planning is STATELESS: routes are found from the current form values via the org-level
// /ifr/routes/find endpoint — no saved entity and no auto-save needed. "Use this route" simply
// sets the ifpsRoute form value (marked dirty), which is persisted with the entity on save.
// Only the optional "re-validate current FPL" needs a saved entity, since it validates the
// FPL that was built server-side from the saved data.
function EntityIfrRoutePlanner<Entity extends FieldValues>({
  displayInfoInput,
  entityType,
  edittedEntity,
  setValue,
}: EntityIfrRoutePlannerProps<Entity>) {
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showRoutePicker, setShowRoutePicker] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState<ValidateFplResponse["validation"] | undefined>(undefined);

  const entity = edittedEntity as unknown as Flight | FlightPlan;

  //Route planning is IFR-only.
  if (!entity.flightRules || !isIfrFlightRules(entity.flightRules)) {
    return null;
  }

  const entityLabel = entityType === "Flight" ? "flight" : "flight plan";
  const ifpsRoute = entity.ifpsRoute;

  const icaoOrUndefined = (name?: string): string | undefined =>
    name && /^[A-Z]{4}$/.test(name.toUpperCase().trim()) ? name.toUpperCase().trim() : undefined;

  const getDepartureDestinationIcao = (): { departure?: string; destination?: string } => {
    if (entityType === "Flight") {
      const f = entity as Flight;
      if (f.route) {
        return { departure: icaoOrUndefined(f.route.departure?.name), destination: icaoOrUndefined(f.route.destination?.name) };
      }
      if (f.customRoute) {
        return {
          departure: f.customRoute.departure?.isIcao ? f.customRoute.departure.icaoCode : undefined,
          destination: f.customRoute.destination?.isIcao ? f.customRoute.destination.icaoCode : undefined,
        };
      }
      return {};
    }
    const fp = entity as FlightPlan;
    return {
      departure: fp.departure?.isIcao ? fp.departure.icaoCode : undefined,
      destination: fp.destination?.isIcao ? fp.destination.icaoCode : undefined,
    };
  };

  const { departure, destination } = getDepartureDestinationIcao();

  const aircraft = entity.aircraft;
  //From the entity only — IFR Details holds these, copied from the aircraft when it was picked. No
  //aircraft fallback: planning on values the user never saw yields fuel/time figures they can't account
  //for. If they are missing, the readiness alert below says so and points at the section that holds them.
  const fuelPerHour = Number(entity.ifrFuelPerHour);
  const maxMass = Number(entity.ifrMaxMass);
  const cruiseSpeedKt = Number(entity.cruiseSpeed?.value ?? aircraft?.cruiseSpeed?.value);
  const level =
    entity.cruiseLevel && (entity.cruiseLevel.unit === "F" || entity.cruiseLevel.unit === "A")
      ? Number(entity.cruiseLevel.value)
      : undefined;

  const missingIfrAlternate = !(entity.altn1 && entity.altn1.length > 0);
  const missingIfrPerformance = !fuelPerHour || !maxMass;
  //Z (VFR first) files "VFR" as its initial level — the IFR level lives inside the route string,
  //so a missing/VFR cruise level does not block planning for Z.
  const hasVfrCruiseLevel = entity.flightRules === "Z" ? false : entity.cruiseLevel?.unit === "VFR" || !level;
  const missingDepDest = !departure || !destination;

  //Soft readiness checks — the aircraft flag and equipment are advisory; IFPS validation stays the
  //hard authority. The FILED Field 10a codes are the source of truth (Y = 8.33 kHz, G = GNSS/RNAV,
  //R = PBN), so an aircraft equipped via those codes must NOT be flagged even if the standalone
  //is833Capable / PBN fields happen to be blank.
  const ifrCertified = aircraft?.ifrCertified === true;
  const equipmentGaps: string[] = [];
  if (aircraft && ifrCertified) {
    const equip = (entity.equipmentCodes ?? aircraft.equipmentCodes ?? "").toUpperCase();
    const has833 = aircraft.is833Capable === true || equip.includes("Y");
    if (!has833) equipmentGaps.push("8.33 kHz radio");
    const surv = (entity.surveillanceEquipmentCodes ?? aircraft.surveillanceEquipmentCodes ?? "").toUpperCase().trim();
    if (!surv || surv === "N") equipmentGaps.push("Mode S transponder (Field 10b)");
    const pbn = (entity.pbn ?? aircraft.pbnCapabilities ?? "").trim();
    //HARD rule, mirroring the FPL builder (functions preflight/fpl.ts): Field 10a "R" declares PBN
    //APPROVAL, and ICAO then requires a PBN/ descriptor in Field 18. Reading R as evidence of
    //capability used to hide the one input that guarantees a rejection — an aircraft with R and no
    //PBN/ passed this advisory and then failed at build time with exactly this error. Keep the two
    //checks distinct: this one blocks filing, the next is only a hint.
    if (equip.includes("R") && !pbn) {
      equipmentGaps.push("PBN/ descriptor — required when Field 10a contains R");
    }
    //HINT: no area navigation declared anywhere — no PBN/, no GNSS (G), no PBN approval (R).
    const hasRnav = pbn.length > 0 || equip.includes("G") || equip.includes("R");
    if (!hasRnav) equipmentGaps.push("PBN / RNAV capability");
  }

  const isLocked = displayInfoInput.disabled ?? false;

  const canPlan = !isLocked && aircraft !== undefined && !missingDepDest && !missingIfrPerformance && !hasVfrCruiseLevel;

  //"Engaged" = the user has started entering a route, so the DEP/DEST step and its readiness alerts
  //are relevant. Flights begin clean (no customRoute) showing only the selector; picking a saved route
  //or "Custom Route" engages them. Flight plans hold DEP/DEST directly, so they are always engaged.
  const flightCustomRoute = entityType === "Flight" ? (entity as Flight).customRoute : undefined;
  const engaged = entityType !== "Flight" || flightCustomRoute !== undefined || !!ifpsRoute;

  //"Custom Route": reveal the DEP/DEST step by creating an empty customRoute (flights only) — the same
  //object the VFR "Custom Route" button creates. The DEP/DEST section is section-gated on its presence.
  const enterCustomMode = () => {
    setValue(
      "customRoute" as Path<Entity>,
      {
        departure: { isIcao: true, icaoCode: "", coords: "" },
        destination: { isIcao: true, icaoCode: "", coords: "" },
        waypoints: [],
        intermediateStops: [],
      } as PathValue<Entity, Path<Entity>>,
      { shouldDirty: true }
    );
  };

  //`route` set on an IFR flight = the Field-15 came from the saved library route, which was planned and
  //IFPS-validated against its own DEP/DEST/ALTN/level. Those stay locked while it is in use (editing one
  //would invalidate the very route being relied on), and the flights list keeps showing the route's name.
  const usingSavedRoute = entityType === "Flight" && (entity as Flight).route !== undefined;

  //"Use as custom route": keep everything exactly as it is — same Field 15, same aerodromes, same level —
  //but drop the link to the library route so the fields unlock. The flight then reads as "Custom" in the
  //list, which is honest once it is no longer the saved route.
  const detachFromSavedRoute = () => {
    const keepRoute = ifpsRoute;
    const f = entity as Flight;
    //DEP/DEST must survive in customRoute — that is where they live once `route` is gone.
    if (!f.customRoute && f.route) {
      setValue(
        "customRoute" as Path<Entity>,
        {
          departure: { isIcao: true, icaoCode: f.route.departure?.name ?? "", coords: getFplCoordsFromRoutePoint(f.route.departure) },
          destination: { isIcao: true, icaoCode: f.route.destination?.name ?? "", coords: getFplCoordsFromRoutePoint(f.route.destination) },
          waypoints: [],
          intermediateStops: [],
        } as PathValue<Entity, Path<Entity>>,
        { shouldDirty: true }
      );
    }
    //Clearing `route` trips the stale-route watcher, which wipes ifpsRoute — put it straight back, since
    //nothing about the routing actually changed. The watcher runs synchronously, so this ordering holds.
    setValue("route" as Path<Entity>, undefined as PathValue<Entity, Path<Entity>>, { shouldDirty: true });
    setValue(displayInfoInput.key, keepRoute as PathValue<Entity, Path<Entity>>, { shouldDirty: true });
  };

  const findIfrRoutes = async (optimizes: string[], levels?: number[], overrides?: IfrPlanningOverrides): Promise<IfrRouteResult[]> => {
    const resp = await AviTracerApi.findIfrRoutesFromData(buildFindInput({ optimizes, levels, ...overrides }));
    return resp.results;
  };

  const buildFindInput = (extra?: Partial<IfrRouteFindInput>): IfrRouteFindInput => ({
    departure: departure!,
    destination: destination!,
    departureDateTime: entity.departureDateTime,
    alternate1: entity.altn1 || undefined,
    alternate2: entity.altn2 || undefined,
    level,
    flightRules: entity.flightRules === "V" ? undefined : entity.flightRules,
    aircraft: {
      registration: aircraft!.registration,
      icaoType: aircraft!.icaoType,
      equipmentCodes: entity.equipmentCodes ?? "S",
      surveillanceEquipmentCodes: entity.surveillanceEquipmentCodes ?? "N",
      cruiseSpeedKt,
      fuelUnit: aircraft?.fuelUnit,
      fuelPerHour,
      maxMass,
    },
    ...extra,
  });

  //Checks a hand-written Field-15 route verbatim (training / company routings).
  const checkManualRoute = async (field15: string, overrides?: IfrPlanningOverrides): Promise<IfrRouteResult> => {
    const resp = await AviTracerApi.checkIfrRouteFromData({ ...buildFindInput({ ...overrides }), field15 });
    return resp.result;
  };

  //Selection is a plain form-value change — persisted together with the entity on save. Any planning
  //inputs changed in the planner (DEP/DEST/ALTN/date-time) are transferred back into the form too,
  //so the flight matches the route that was actually planned.
  const selectIfrRoute = async (
    field15: string,
    overrides?: IfrPlanningOverrides,
    //Optional DEP/DEST coords (from a saved route's lat/lon). Pre-filling them means the DEP/DEST point
    //fields don't geocode-and-fire on mount — that mount-time onChange would otherwise trip the watcher
    //that clears ifpsRoute, wiping the route just selected (the "first pick does nothing" bug).
    pointCoords?: { departure?: string; destination?: string }
  ): Promise<IfrRouteSelectResult> => {
    const set = (key: string, value: any) => setValue(key as Path<Entity>, value as PathValue<Entity, Path<Entity>>, { shouldDirty: true });

    //Any route arriving through here is planner-built, so it is no longer the saved library route — drop
    //the link, which unlocks DEP/DEST/ALTN/level and makes the list read "Custom". Done FIRST: clearing
    //`route` trips the stale-route watcher, and ifpsRoute is written last (below), so it survives.
    //(pickSavedRoute attaches its route before calling this, for the same ordering reason.)
    if (entityType === "Flight" && (entity as Flight).route) set("route", undefined);

    //Empty field15 = disarm: clear the selected route (persisted on save like any form change).
    if (!field15) {
      set(displayInfoInput.key, "");
      return { ifpsRoute: undefined };
    }

    //IMPORTANT: write the route (ifpsRoute) LAST. Changing cruiseLevel / DEP / DEST triggers the
    //EntityDetailsPage watcher that clears a stale ifpsRoute — so those must be applied FIRST (clearing
    //the OLD route), and the new route set afterwards so it sticks. Setting the route first would let the
    //level/DEP/DEST writes wipe the route we just selected (e.g. a saved route with a different level).
    if (overrides) {
      const icao = (v?: string | Date) => {
        const s = (v ?? "").toString().toUpperCase().trim();
        return /^[A-Z]{4}$/.test(s) ? s : undefined;
      };
      const ovDep = icao(overrides.departure);
      const ovDest = icao(overrides.destination);
      const ovAltn1 = icao(overrides.alternate1);
      const ovAltn2 = icao(overrides.alternate2);

      if (ovAltn1 && ovAltn1 !== entity.altn1) set("altn1", ovAltn1);
      if (ovAltn2 && ovAltn2 !== entity.altn2) set("altn2", ovAltn2);
      const ovLvl = Number(overrides.level);
      if (Number.isFinite(ovLvl) && ovLvl > 0 && ovLvl !== level) {
        set("cruiseLevel", { unit: "F", value: String(Math.round(ovLvl)).padStart(3, "0") });
      }
      //Store the route distance (aircraft-independent) so the EET can be computed on the fly from it
      //and the flight's cruise speed — the same "Set EET" mechanism VFR uses.
      const ovDist = Number(overrides.distanceNM);
      if (Number.isFinite(ovDist) && ovDist > 0) {
        set("ifrDistanceNM", ovDist);
      }
      if (overrides.departureDateTime) {
        const d = new Date(overrides.departureDateTime);
        if (!isNaN(d.getTime()) && d.getTime() !== new Date(entity.departureDateTime).getTime()) {
          set("departureDateTime", d);
        }
      }

      //DEP/DEST: flight plans hold them directly; IFR flights hold them in customRoute. Flights based
      //on a saved VFR route are left untouched (change those via the route picker).
      if (entityType === "Flight Plan") {
        const fp = entity as FlightPlan;
        if (ovDep && !(fp.departure?.isIcao && fp.departure.icaoCode === ovDep)) {
          set("departure", { isIcao: true, icaoCode: ovDep, coords: pointCoords?.departure ?? "" });
        }
        if (ovDest && !(fp.destination?.isIcao && fp.destination.icaoCode === ovDest)) {
          set("destination", { isIcao: true, icaoCode: ovDest, coords: pointCoords?.destination ?? "" });
        }
      } else {
        const f = entity as Flight;
        //A fresh IFR flight starts with no customRoute (clean route-selector state). Picking a route
        //creates it, which also reveals the DEP/DEST step below — mirroring the VFR custom-route reveal.
        if (!f.customRoute) {
          set("customRoute", {
            departure: { isIcao: true, icaoCode: ovDep ?? "", coords: pointCoords?.departure ?? "" },
            destination: { isIcao: true, icaoCode: ovDest ?? "", coords: pointCoords?.destination ?? "" },
            waypoints: [],
            intermediateStops: [],
          });
        } else {
          if (ovDep && !(f.customRoute.departure?.isIcao && f.customRoute.departure.icaoCode === ovDep)) {
            set("customRoute.departure", { isIcao: true, icaoCode: ovDep, coords: pointCoords?.departure ?? "" });
          }
          if (ovDest && !(f.customRoute.destination?.isIcao && f.customRoute.destination.icaoCode === ovDest)) {
            set("customRoute.destination", { isIcao: true, icaoCode: ovDest, coords: pointCoords?.destination ?? "" });
          }
        }
      }
    }

    //Route set LAST (see note above) so the level/DEP/DEST writes above don't clear it.
    set(displayInfoInput.key, field15);
    return { ifpsRoute: field15 };
  };

  //Selecting a saved IFR/mixed route from the library: map its Field-15, DEP/DEST, level, alternates
  //and distance into the flight via the same selectIfrRoute mapping used when arming from the planner.
  const pickSavedRoute = (route: Route) => {
    setShowRoutePicker(false);
    if (!route.ifpsRoute) return;
    //Pass the saved route's DEP/DEST coords (from its stored lat/lon) so the point fields mount already
    //geocoded — otherwise their mount-time geocode fires an onChange that clears the just-set ifpsRoute.
    const depCoords = route.departure ? getFplCoordsFromRoutePoint(route.departure) : undefined;
    const destCoords = route.destination ? getFplCoordsFromRoutePoint(route.destination) : undefined;
    //Attach the library route so the flights list shows its NAME instead of "Custom", and so the
    //aerodromes/level below read as belonging to it (and lock). Written BEFORE selectIfrRoute: changing
    //"route" trips the stale-route watcher, and selectIfrRoute writes ifpsRoute last, so it survives.
    if (entityType === "Flight") {
      setValue("route" as Path<Entity>, route as PathValue<Entity, Path<Entity>>, { shouldDirty: true });
    }
    selectIfrRoute(
      route.ifpsRoute,
      {
        departure: route.departure?.name,
        destination: route.destination?.name,
        alternate1: route.altn1,
        alternate2: route.altn2,
        level: route.defaultLevel,
        distanceNM: route.distanceNM,
      },
      { departure: depCoords, destination: destCoords }
    );
  };

  //Library routes selectable for this flight: IFR-family with a Field-15, matching the flight's rules.
  const routePickerFilter = (r: Route) =>
    r.isIfr === true && !!r.ifpsRoute && (r.flightRules ?? "I") === (entity.flightRules === "V" ? "I" : entity.flightRules);

  const validateFpl =
    entity.id !== undefined
      ? async (): Promise<ValidateFplResponse> => {
          return entityType === "Flight" ? AviTracerApi.validateFpl(entity.id!) : AviTracerApi.validateFlightPlanFpl(entity.id!);
        }
      : undefined;

  //On the ready-to-file page the FPL is already built — validate it directly rather than opening the planner.
  const handleValidate = async () => {
    if (!validateFpl) return;
    setValidating(true);
    setValidation(undefined);
    try {
      const resp = await validateFpl();
      setValidation(resp.validation);
    } catch (e: any) {
      setValidation({ result: "errors", validation: typeof e === "string" ? e : e?.message ?? "Validation failed.", errors: [] });
    } finally {
      setValidating(false);
    }
  };

  return (
    <div className="border rounded-lg p-3">
      <div className="mb-2">
        <strong>{displayInfoInput.name}</strong>
      </div>

      {!engaged ? (
        //Clean state: just the VFR-style route selector — pick a saved (rules-matching) IFR route, or
        //"Custom Route" to build one. No DEP/DEST and no readiness alerts until the user commits.
        <>
          <Row className="mb-2">
            <Col xs={8} lg={9}>
              <ReadOnlyInputBox isDisabled={isLocked} value="" name="Route" onClick={() => setShowRoutePicker(true)} />
            </Col>
            <Col xs={4} lg={3}>
              <Button disabled={isLocked} variant="dark" onClick={enterCustomMode} className="h-100 w-100">
                <div>Custom</div> Route
              </Button>
            </Col>
          </Row>
          <small className="text-muted">Pick a saved IFR route, or choose Custom Route to build one.</small>
        </>
      ) : (
        //Engaged (Custom Route pressed, or a saved route picked): the classic layout — readiness alerts,
        //the selected Field-15, and a full-width Plan / Re-plan button. No route selector. This planner
        //input renders at the BOTTOM of the Route section, so it sits after the DEP/DEST/ALTN fields.
        <>
          {missingDepDest && (
            <Alert variant="danger" className="py-2">
              <small>IFR route-finding requires ICAO departure and destination aerodromes.</small>
            </Alert>
          )}

          {missingIfrAlternate && (
            <Alert variant="danger" className="py-2">
              <small>An alternate aerodrome (ALTN) is required for IFR flight plans.</small>
            </Alert>
          )}

          {missingIfrPerformance && (
            <Alert variant="danger" className="py-2">
              <small>Enter fuel burn / MTOW in the IFR Details section — required for IFR route-finding.</small>
            </Alert>
          )}

          {hasVfrCruiseLevel && (
            <Alert variant="danger" className="py-2">
              <small>IFR flights require a cruising flight level (e.g. FL080).</small>
            </Alert>
          )}

          {aircraft && !ifrCertified && (
            <Alert variant="warning" className="py-2">
              <small>
                {aircraft.name} is not marked as IFR certified. If it is, enable it in Aircraft → IFR / PBN.
              </small>
            </Alert>
          )}

          {equipmentGaps.length > 0 && (
            <Alert variant="warning" className="py-2">
              <small>IFPS validation may fail — missing: {equipmentGaps.join(", ")}. Check Aircraft → IFR / PBN.</small>
            </Alert>
          )}

          {ifpsRoute ? (
            <div className="mb-2">
              <small className="text-muted">Selected route (Field 15)</small>
              <div className="font-monospace small border rounded p-2 bg-light">{ifpsRoute}</div>
            </div>
          ) : (
            <div className="mb-2">
              <small className="text-muted">No IFR route selected yet — plan a route with EUROCONTROL before filing.</small>
            </div>
          )}

          <div className="d-grid gap-2">
            <Button variant="dark" disabled={!canPlan} onClick={() => setShowPlanModal(true)}>
              {ifpsRoute ? "Re-plan IFR route" : "Plan IFR route"}
            </Button>
            {/* Even with a route already selected/planned, allow swapping to a different saved route. */}
            <Button variant="outline-dark" disabled={isLocked} onClick={() => setShowRoutePicker(true)}>
              {ifpsRoute ? "Choose a different saved route" : "Choose a saved route"}
            </Button>
            {/* A route is selected on a saved entity: a direct Validate convenience.
                (The final ready-to-file page has its own validate-only section — see BaseFplPage.) */}
            {ifpsRoute && validateFpl && (
              <Button variant="outline-dark" size="sm" disabled={validating} onClick={handleValidate}>
                {validating ? "Validating…" : "Validate with EUROCONTROL"}
              </Button>
            )}
          </div>

          {validation && (
            <div className="mt-2">
              {validation.result === "ok" ? (
                <Alert variant="success" className="py-2 mb-0">
                  <small>No errors — IFPS valid, ready to file.</small>
                </Alert>
              ) : (
                <Alert variant="danger" className="py-2 mb-0" style={{ whiteSpace: "pre-wrap" }}>
                  <small>{validation.errors.length > 0 ? validation.errors.join("\n") : validation.validation}</small>
                </Alert>
              )}
            </div>
          )}

          {usingSavedRoute ? (
            <div className="mt-2">
              <small className="text-muted d-block">
                Departure, destination, alternates and cruising level come from this saved route and were
                validated with it, so they are locked here. To change them, use it as a custom route.
              </small>
              <Button variant="outline-dark" size="sm" className="mt-2" disabled={isLocked} onClick={detachFromSavedRoute}>
                Use as custom route (keeps everything)
              </Button>
            </div>
          ) : (
            <small className="fw-light text-muted">
              <i>The selected route — and any DEP/DEST/ALTN/time changes made while planning — are saved together with the {entityLabel}.</i>
            </small>
          )}
        </>
      )}

      <Modal show={showRoutePicker} onHide={() => setShowRoutePicker(false)} fullscreen>
        <Modal.Header closeButton>
          <Modal.Title>Choose a saved {entity.flightRules === "Y" || entity.flightRules === "Z" ? "mixed" : "IFR"} route</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          <ListRoutesPage routeFilter={routePickerFilter} onRouteSelected={pickSavedRoute} />
        </Modal.Body>
      </Modal>

      {showPlanModal && (
        <PlanIfrRouteModal
          show={showPlanModal}
          onHide={() => setShowPlanModal(false)}
          isIfr={true}
          ifpsRoute={ifpsRoute}
          baseLevel={level}
          flightRules={entity.flightRules}
          cruiseSpeedKt={cruiseSpeedKt}
          departureDateTime={entity.departureDateTime ? moment(entity.departureDateTime).toDate() : undefined}
          departure={departure}
          destination={destination}
          alternate1={entity.altn1}
          alternate2={entity.altn2}
          findIfrRoutesAction={findIfrRoutes}
          checkManualRouteAction={checkManualRoute}
          selectIfrRouteAction={selectIfrRoute}
          validateFplAction={validateFpl}
          onRouteSelected={() => {}}
          persistedOnSelect={false}
        />
      )}
    </div>
  );
}

export default EntityIfrRoutePlanner;
